import { Component, DestroyRef, OnInit, Renderer2, inject, ChangeDetectionStrategy } from "@angular/core";
import { AsyncPipe } from "@angular/common";
import { RouterOutlet } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
// Moment timezone
import Moment from "moment-timezone";
import { defineLocale } from "ngx-bootstrap/chronos";
import { BsDatepickerConfig, BsDaterangepickerConfig, BsLocaleService } from "ngx-bootstrap/datepicker";
import { arLocale, deLocale, enGbLocale, frLocale, nlLocale } from "ngx-bootstrap/locale";
// Models
import { AuthenticationService } from "./services/authentication/authentication.service";
import { applyMomentLocale, resolveMomentLocale } from "./common/momentLocaleHelper";
import { syncDatepickerWeekNumbers } from "./common/datepickerWeekNumbers";
import { setBooleanCellLabels } from "./common/booleanCellLabels";
import { filter, map, tap } from "rxjs/operators";
import { NavigationEnd, Router, ActivatedRoute, ResolveEnd, ResolveStart } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { NgSelectConfig } from "@ng-select/ng-select";
import { setTheme } from "ngx-bootstrap/utils";
import { timer } from "rxjs";
import { ColorService } from "./services/common/color.service";

window["moment"] = Moment;
import "moment/locale/nl";
import "moment/locale/de";
import "moment/locale/fr";
import "moment/locale/ar";
import "moment/locale/es";
import "moment/locale/ru";
import "moment/locale/zh-cn";
import "moment/locale/en-gb";

defineLocale("nl", nlLocale);
defineLocale("de", deLocale);
// defineLocale('en', ); // when undefined it takes default
defineLocale("gb", enGbLocale);
defineLocale("ar", arLocale);
defineLocale("fr", frLocale);

// locale ar
import { registerLocaleData } from "@angular/common";
import localeNl from "@angular/common/locales/nl";
import localeDe from "@angular/common/locales/de";
import localeEn from "@angular/common/locales/en";
import localeAr from "@angular/common/locales/ar";
import localeFr from "@angular/common/locales/fr";

registerLocaleData(localeNl);
registerLocaleData(localeDe);
registerLocaleData(localeEn);
registerLocaleData(localeAr);
registerLocaleData(localeFr);

@Component({
  selector: "fh-app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet, AsyncPipe],
})
export class AppComponent implements OnInit {
  private config = inject(NgSelectConfig);
  private colorService = inject(ColorService);
  private localeService = inject(BsLocaleService);
  private renderer = inject(Renderer2);
  private translate = inject(TranslateService);
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private titleService = inject(Title);
  private destroyRef = inject(DestroyRef);
  private datepickerConfig = inject(BsDatepickerConfig);
  private daterangepickerConfig = inject(BsDaterangepickerConfig);

  title = "fleet-management";
  isLoading = true;
  previousUrl: string;

  countSub$ = timer(0, 1000).pipe(
    tap((_) => (window["reloadTime"] = window["reloadTime"] || 1)),
    map((_) => --window["reloadTime"]),
  );

  constructor() {
    const translate = this.translate;

    setTheme("bs4"); // or 'bs5'
    syncDatepickerWeekNumbers(this.localeService, [this.datepickerConfig, this.daterangepickerConfig], this.destroyRef);

    this.checkRoute();

    // Redirect SSO callback url (backend-driven SSO; do not gate on server_variables.SSOEnabled)
    {
      const configuredRedirect = window["server_variables"]?.SSORedirectUrl as string | undefined;
      let callBackUrl = configuredRedirect ? configuredRedirect.replace(window.location.origin, "") : "";
      if (callBackUrl == "") {
        callBackUrl = "/Auth-callback";
      }
      if (window.location.pathname == callBackUrl || window.location.search.includes("code")) {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        const iss = params.get("iss");
        void this.router.navigate([callBackUrl], { queryParams: { code: code, state: state, iss: iss } });
      }
    }

    // Set the token and handle refresh logic globally
    $(document).on("ajaxSend", (event, req: JQueryXHR, settings: JQueryAjaxSettings) => {
      // eslint-disable-next-line @typescript-eslint/unbound-method -- jQuery default xhr factory does not use `this`
      const origXhrFunc = settings.xhr;

      settings.xhr = () => {
        const xhr = origXhrFunc();

        // Store the original send function
        // eslint-disable-next-line @typescript-eslint/unbound-method -- re-bound below via origSend.apply(xhr, args)
        const origSend = xhr.send;

        // Override the send function to delay its execution
        xhr.send = (...args) => {
          if (xhr.readyState === 1) {
            // Add token to request headers if it's available
            const isSSO = this.authenticationService.user !== null;
            const isImpersonated = this.authenticationService.getIsImpersonated();

            if (isSSO == false && !this.authenticationService.isTokenValid()) {
              // Return a new observable to handle the token refresh logic
              this.authenticationService.refreshTokenHandler(this.authenticationService.getRedirectPath(settings.url), isImpersonated).subscribe({
                next: (result) => {
                  if (result) {
                    let token = this.authenticationService.getAuthToken();

                    // When impersonating, use impersonating token
                    if (!isImpersonated && this.authenticationService.user) {
                      token = this.authenticationService.user.id_token;
                    }
                    // Update the authorization header on the xhr object directly
                    xhr.setRequestHeader("Authorization", "Bearer " + token);
                    origSend.apply(xhr, args);
                  } else {
                    xhr.abort();
                  }
                },
                error: (error) => {
                  console.error("Error during token refresh", error);
                  xhr.abort();
                },
              });
            } else if (isSSO == true) {
              if (!isImpersonated && !this.authenticationService.isSSOTokenValid()) {
                this.authenticationService.refreshSSOTokenHandler(this.authenticationService.getRedirectPath(settings.url)).subscribe({
                  next: (result) => {
                    if (result) {
                      let token = this.authenticationService.getAuthToken();

                      // When impersonating, use impersonating token
                      if (!isImpersonated && this.authenticationService.user) {
                        token = this.authenticationService.user.id_token;
                      }
                      // Update the authorization header on the xhr object directly
                      xhr.setRequestHeader("Authorization", "Bearer " + token);
                      origSend.apply(xhr, args);
                    } else {
                      xhr.abort();
                    }
                  },
                  error: (error) => {
                    console.error("Error during token refresh", error);
                    xhr.abort();
                  },
                });
              } else if (isImpersonated && !this.authenticationService.isTokenValid()) {
                // when impersonating and the token is invalid -> refresh
                this.authenticationService.refreshTokenHandler(this.authenticationService.getRedirectPath(settings.url), isImpersonated).subscribe({
                  next: (result) => {
                    if (result) {
                      let token = this.authenticationService.getAuthToken();

                      // When impersonating, use impersonating token
                      if (!isImpersonated && this.authenticationService.user) {
                        token = this.authenticationService.user.id_token;
                      }
                      // Update the authorization header on the xhr object directly
                      xhr.setRequestHeader("Authorization", "Bearer " + token);
                      origSend.apply(xhr, args);
                    } else {
                      xhr.abort();
                    }
                  },
                  error: (error) => {
                    console.error("Error during token refresh", error);
                    xhr.abort();
                  },
                });
              } else {
                // SSO Token is still valid, directly set the authorization header and send the request
                let token = this.authenticationService.getAuthToken();

                // When impersonating, use impersonating token
                if (!isImpersonated && this.authenticationService.user) {
                  token = this.authenticationService.user.id_token;
                }
                xhr.setRequestHeader("Authorization", "Bearer " + token);
                origSend.apply(xhr, args);
              }
            } else {
              // Token is still valid, directly set the authorization header and send the request
              let token = this.authenticationService.getAuthToken();

              // When impersonating, use impersonating token
              if (!isImpersonated && this.authenticationService.user) {
                token = this.authenticationService.user.id_token;
              }
              xhr.setRequestHeader("Authorization", "Bearer " + token);
              origSend.apply(xhr, args);
            }
          }
        };
        return xhr;
      };
    });

    this.config.notFoundText = "No data found";
    // this.config.appendTo = 'body';
    // this.config.bindValue = 'value';

    if (!translate.currentLang) {
      let currentSavedLang = this.authenticationService.getCultureLang();

      if (currentSavedLang === "" || currentSavedLang == null) {
        currentSavedLang = "en-US";

        if (window["server_variables"].Debug) {
        }
      }

      console.log(`[App] Setting language: ${currentSavedLang}`);
      translate.use(currentSavedLang.substring(0, 2));

      const localeVar = resolveMomentLocale(currentSavedLang);
      applyMomentLocale(localeVar);

      console.log(`[App] Moment locale set to: ${Moment.locale()}`);

      this.localeService.use(localeVar);

      translate.onLangChange.subscribe((event) => {
        if (event.lang.indexOf("ar") > -1) {
          this.renderer.addClass(document.body, "rtls");
        } else {
          this.renderer.removeClass(document.body, "rtls");
        }
        setBooleanCellLabels(translate.instant("general.true"), translate.instant("general.false"));
        this.updatePageTitle();
      });
    }

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updatePageTitle();
    });
  }

  private updatePageTitle(): void {
    let child = this.activatedRoute.firstChild;
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
      } else if (child.snapshot.data && (child.snapshot.data["titleKey"] || child.snapshot.data["title"])) {
        const titleKey = child.snapshot.data["titleKey"];
        const title = child.snapshot.data["title"];
        const id = child.snapshot?.params?.["id"];
        const titleText = titleKey ? this.translate.instant(titleKey) : title;
        this.titleService.setTitle("[" + titleText + (id ? ": " + id : "") + "] - [Fleet Management]");
        return;
      } else {
        return;
      }
    }
  }

  checkRoute() {
    // Apply the theme the url implies, but only as a default for when we do not know the user yet
    // (the login page). A stored user theme always wins - it is applied by the APP_INITIALIZER in
    // app.config.ts and by the layout components - so a user on a branded url can still choose a
    // different theme in their profile (SUP-4968).

    // Older builds parked the url theme in the impersonation slot, where getWhitelabel() outranks
    // the real user theme and nothing but stopImpersonation() ever cleared it. Drop that leftover
    // for sessions that are not actually impersonating, otherwise those users stay stuck.
    if (!this.authenticationService.getIsImpersonated()) {
      localStorage.removeItem(AuthenticationService.WHITELABEL_IMPERSONATE);
    }

    if (this.authenticationService.getWhitelabel()) {
      return;
    }

    const urlTheme = this.colorService.getDefaultThemeForUrl(window.location.origin);
    if (urlTheme === "") {
      return;
    }

    localStorage.setItem(AuthenticationService.WHITELABEL, urlTheme);

    this.colorService.getThemeColors(urlTheme).subscribe((result) => {
      this.colorService.addTheme(result);
    });
  }

  ngOnInit() {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.isLoading = false;
      }
      if (e instanceof ResolveStart) {
      }
      if (e instanceof ResolveEnd) {
        this.isLoading = false;
      }
    });
  }
}
